import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Edit2, Shield, Check, X, Loader2, UserX, UserCheck } from 'lucide-react';
import axios from 'axios';
import logger from '../../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800' },
  content_admin: { label: 'Content Admin', color: 'bg-blue-100 text-blue-800' },
  finance_admin: { label: 'Finance Admin', color: 'bg-green-100 text-green-800' },
  events_admin: { label: 'Events Admin', color: 'bg-purple-100 text-purple-800' },
  viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-700' },
};

const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'viewer', primary_track_id: '' });
  const [tracks, setTracks] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    // Load training tracks once for the primary-track picker.
    axios.get(`${BACKEND_URL}/api/admin/docs`, { withCredentials: true })
      .then((r) => setTracks(r.data?.certification_tracks || []))
      .catch(() => setTracks([]));
  }, []);

  const handleSave = async () => {
    if (!form.name || !form.email || (!editingId && !form.password)) return;
    setSaving(true);
    try {
      if (editingId) {
        const payload = { name: form.name, role: form.role,
          primary_track_id: form.primary_track_id || null };
        if (form.password) payload.password = form.password;
        await axios.put(`${BACKEND_URL}/api/admin/users/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/users`, form, { withCredentials: true });
      }
      setForm({ name: '', email: '', password: '', role: 'viewer', primary_track_id: '' });
      setShowForm(false);
      setEditingId(null);
      fetchUsers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save user');
    }
    setSaving(false);
  };

  const handleEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: '', role: u.role,
      primary_track_id: u.primary_track_id || '' });
    setEditingId(u.user_id);
    setShowForm(true);
  };

  const handleToggleActive = async (u) => {
    const action = u.is_active !== false ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${action === 'deactivate' ? 'Deactivate' : 'Reactivate'} ${u.name}?`)) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${u.user_id}`, { is_active: u.is_active === false }, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">User Management</h2>
          <p className="text-xs text-[#4B5563]">{users.length} admin user{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ name: '', email: '', password: '', role: 'viewer' }); }}
          className="btn-primary text-xs flex items-center gap-1.5" data-testid="add-user-btn">
          <Plus className="w-3.5 h-3.5" /> Add User
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="user-form">
          <h3 className="font-heading font-semibold text-sm mb-4">{editingId ? 'Edit User' : 'Create New User'}</h3>
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="user-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editingId}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none disabled:bg-gray-50"
                data-testid="user-email-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">
                Password {editingId ? '(leave blank to keep current)' : '*'}
              </label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingId ? 'Unchanged' : 'Min 8 characters'}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="user-password-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Role *</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="user-role-select">
                {Object.entries(ROLE_LABELS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">
                Primary training track
                <span className="text-[10px] text-[#9CA3AF] font-normal ml-1">
                  (assigns the onboarding path)
                </span>
              </label>
              <select value={form.primary_track_id || ''}
                onChange={(e) => setForm({ ...form, primary_track_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="user-primary-track-select">
                <option value="">— No track assigned —</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.audience})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="save-user-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {editingId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="btn-secondary text-xs flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">User</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Role</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Status</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#4B5563]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const roleInfo = ROLE_LABELS[u.role] || ROLE_LABELS.viewer;
              const isActive = u.is_active !== false;
              return (
                <tr key={u.user_id} className={`border-b border-[#F1F5F9] last:border-0 ${!isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#BA1826] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {u.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-[#0A0A0A]">{u.name}</p>
                        <p className="text-xs text-[#9CA3AF]">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded ${roleInfo.color}`}>
                      <Shield className="w-3 h-3" /> {roleInfo.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${isActive ? 'text-green-600' : 'text-red-500'}`}>
                      {isActive ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(u)} className="p-1.5 text-[#4B5563] hover:text-[#BA1826] rounded" title="Edit" data-testid={`edit-user-${u.user_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleToggleActive(u)}
                        className={`p-1.5 rounded ${isActive ? 'text-[#4B5563] hover:text-red-600' : 'text-[#4B5563] hover:text-green-600'}`}
                        title={isActive ? 'Deactivate' : 'Reactivate'}
                        data-testid={`toggle-user-${u.user_id}`}>
                        {isActive ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsersTab;
